<script>
    import { slide, fly } from 'svelte/transition';
    import { Tooltip } from 'sveltestrap';
    import { tooltip } from "@svelte-plugins/tooltips";

    import { Chart, registerables } from 'chart.js';

    import AimTooltip from '../../component/AimTooltip.svelte';
    import { formatDate, formatDateTime, dateDescriptionStringAddExtraDescriptor } from '../../date-time.js';
    import { vptServer, token, inmateDrawerOpen, getTooltipDisplayOptions } from '../../stores.js';
    import { referenceDataCellStatusList,  getReferenceDataSiteName, referenceDataRiskFactorTabList, 
        getReferenceDataItemFromList } from "../../reference-data.js";
    import { getRiskFactorsTooltip } from '../../risk-factors.js';

    let chart;
    let chartLabels = ["Sociodemographic", "Criminological", "Clinical", "Custodial" ,"Historical"];
    Chart.register(...registerables);

    export let id;
    export let todaysConfig;

    let history = [];
    let tabbedHistory = [];
    let summaryTabId = referenceDataRiskFactorTabList[0].vptRiskFactorTabUrn;
    let selectedTabId = referenceDataRiskFactorTabList[0].vptRiskFactorTabUrn;
    let searchCompleted = false;

    let selectedSeverityGroup;
    let selectedComment = null;

    $: {
        if (todaysConfig) {
            history = null;
            getRiskFactorHistory();
        }
    }

    async function getRiskFactorHistory() {
        searchCompleted = false;
        history = [];
        tabbedHistory = [];
        
        await fetch(`${$vptServer}/inmate/${id}/riskfactor/list`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            let firstCard;
            history = data.slice(0);
            data.forEach((a,index,arr) => {
                        if (firstCard == null) {
                            firstCard = a.riskFactorUrn; 
                        }
                arr[index] = {...a,
                    ...getTabbedAnswers(a.riskFactors),
                    ...getSummaryAnswers(a.riskFactors),
                    overallSignificantBreakdown: generateSignificanceBreakdownForTab(a.riskFactors.filter((factor) => factor.severity)),
                    active: firstCard == a.riskFactorUrn ? true : false};
            });
            tabbedHistory = data;
        })
        .catch(error => {
            console.error("Error getting risk factor history");
            return;
        });
            
        searchCompleted = true;
        setTimeout(() => {renderAssessmentChart(tabbedHistory[0])}, 1);
    }

    function getTabbedAnswers(riskFactors) {
        let tabs = referenceDataRiskFactorTabList.map(tab => {
            let currentSection = tab.section;

            let answers = riskFactors.filter(answer => {
                return (answer.section == currentSection);
            }).map(a => {
                return {question : a.riskFactorTitle, answer : a.answer, notes : a.notes, questionHelpText : a.questionHelpText, section : a.category, weightedValue : a.weightedValue, severity : a.severity, header : a.sectionHeader, immediateTargeted : a.immediateTargeted};
            });

            return { id : tab.vptRiskFactorTabUrn, name : tab.name, answers : splitAnswers(answers), 
                order : tab.ordering, active : tab.ordering == 1, significanceBreakdown : generateSignificanceBreakdownForTab(answers) }
        });

        return { tabs : tabs, displayedAnswers : tabs.find((t) => t.active).answers }
    }

    function getSummaryAnswers(riskFactors) {
        let answers = riskFactors.filter(answer => {
                return (answer.weightedValue > 0 || answer.sectionHeader);
            }).map(a => {
                return {question : a.riskFactorTitle, answer : a.answer, notes : a.notes, section : a.category, weightedValue : a.weightedValue, severity : a.severity, header : a.sectionHeader, clitemUrn : a.clitemUrn, questionHelpText : a.questionHelpText, displayanswercontext : a.displayanswercontext, immediateTargeted : a.immediateTargeted};
            });
    
        return { displayedSummaryAnswers : answers }
    }

    function splitAnswers(answers) {
        if (answers.length == 0) return [[],[]];

        let colLength = answers.length / 2;
        colLength += answers.length % 2;

        let firstColumn = answers.slice(0, colLength);
        let secondColumn = answers.slice(colLength, answers.length + 1);
    
        if (firstColumn[firstColumn.length - 1].header) {
            secondColumn.unshift(firstColumn.pop());
        }

        return [firstColumn, secondColumn];
    }


    function renderAssessmentChart(assessment) {
        if (assessment.datasets) {
            var ctx = document.getElementById("risk-factors-summary-chart-canvas-" + assessment.riskFactorUrn).getContext("2d");

            chart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: chartLabels,
                    datasets: assessment.datasets
                },
                options: {
                    responsive: true,
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: function() {},
                                label: function(context) {
                                    return context.dataset.label + ": " + Math.round(context.parsed.x) + "%";
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            stacked: true,
                            min: 0,
                            max: 100
                        },
                        y: {
                            stacked: true,
                        }
                    }
                }
            });
        }
    }

    function selectTab(assessment, selectedId) {
        selectedTabId = selectedId;

        tabbedHistory.forEach(assessment => {
            assessment.tabs.forEach((tab) => {
                if (tab.id === selectedId) {
                    tab.active = true;
                } else {
                    tab.active = false;
                }
            });

            assessment.displayedAnswers = assessment.tabs.find((t) => t.active).answers;
            tabbedHistory = tabbedHistory;

            if (selectedId == summaryTabId && assessment.active) {
                setTimeout(() => {renderAssessmentChart(assessment)}, 1);
            }
        });
    }
    
    function selectAssessment(assessment) {
        assessment.active = !assessment.active; 
        if (assessment.active && selectedTabId == summaryTabId) setTimeout(() => {renderAssessmentChart(assessment)}, 1)
    } 

    function generateSignificanceBreakdownForTab(answers) {
        let severityBreakdown = []; 

        let significantFactorsCount = 0;
        let mediumFactorsCount = 0;
        let lowFactorsCount = 0;
        let noneFactorsCount = 0;
       
        answers.forEach(answer => {
            if (answer.severity == 'NA') noneFactorsCount++; 
            else if (answer.severity == 'L') lowFactorsCount++;
            else if(answer.severity == 'M') mediumFactorsCount++;
            else if(answer.severity == 'H') significantFactorsCount++;
        });

        if (significantFactorsCount >= 1) {
            severityBreakdown.push({
                id: 'H',
                title: 'High Significance',
                percentage:(significantFactorsCount / answers.length) * 100,
                total: significantFactorsCount,
                colour: '#dc3545'
            })
        };
        if (mediumFactorsCount >= 1) {
            severityBreakdown.push({
                id: 'M',
                title: 'Medium Significance',
                percentage: (mediumFactorsCount / answers.length) * 100,
                total: mediumFactorsCount,
                colour: '#FFA500'
            })
        };
        if (lowFactorsCount >= 1) {
            severityBreakdown.push({
                id: 'L',
                title: 'Low Significance',
                percentage:(lowFactorsCount / answers.length) * 100,
                total: lowFactorsCount,
                colour: '#ffe15d'
            })
        };
        if (noneFactorsCount >= 1) {
            severityBreakdown.push({
                id: "NA",
                title: 'No Significance',
                percentage:(noneFactorsCount / answers.length) * 100,
                total: noneFactorsCount,
                colour: '#e9ecef'
            })
        };

        return severityBreakdown;
    }

    function getSigBreakdownForTab(assessmentId, selectedTabId) {
        let assessment = tabbedHistory.find(a => a.riskFactorUrn === assessmentId);
        let tab = assessment.tabs.find(t => t.id == selectedTabId);
        return tab.significanceBreakdown;
    }
</script>

<style>
    .container-fluid {
        padding-top: 163px;
        min-width: 1100px;
    }

    .container-fluid {
        padding-right: 96px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .container-fluid-short {
        padding-right: 270px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }
    
    .card {
        margin-top: 5px;
    }

    .card-head {    
    	padding: 16px 30px;
        font-size: 15px;
        cursor: pointer;
        border-bottom: none;

    }

    .card-head.collapsed:hover, .card-head.collapsed:focus {
        background-color: #edf3f7;
    }

    .card-head .target-indicator {
        position: absolute;
        height: 16px;
        width: 16px;
        left: 8px;
    }

    .card-head span {
        cursor: default
    }

    .badge {
        margin-right: 5px;
    }

    .progress-container {
        width: 400px;
    }

    .progress-container .progress {
        cursor: pointer;
    }

    .card-head .card-collapse-control {
        position: absolute;
        height: 16px;
        width: 16px;
        right: 8px;
    }

    .risk-factor-details {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-top: 13px;
        padding-bottom: 13px;
        margin-top: 8px;
        font-size: 14px;
        color: #7e8299;
        padding-left: 30px;
        padding-right: 30px;
        border-top: 1px solid #dee2e6;
    }

    .risk-factor-detail {
        display: flex;
        align-items: center;
    }

    .risk-factor-detail img {
        padding-bottom: 2px;
        padding-left: 4px;
    }

    .card-chart-container {
        height: 250px;
        width:30%;
        border-right:3%;
    }

    .overall-card-column {
        width:67%;
    }

    .card-columns-container{
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .no-sig-text {
        color: #e9ecef;
    }

    .low-sig-text {
        color: #ffe15d;
    }

    .med-sig-text {
        color: #FFA500;
    }
    
    .high-sig-text {
        color: #dc3545;
    }

    .progress-container {
        margin-right:5px;
    }

    .selected-row {
        font-weight: 600;
    }

    .hidden-row {
        filter: opacity(30%);
    }

    .overall-card-column  > span, .overall-card-column > div .well {    
        margin-right: 5px;
        margin-bottom: 5px;
    }

    .modal-header {
        padding-top: 10px;
        padding-bottom: 10px;
    }
    .modal h5 {
        font-size: 18px;
    }
    .modal-body {
        padding: 0;
    }
    .modal-body > div {
        padding: 1rem;
    }
    .modal-body .prompt {
        background-color: #edf3f7;
    }
    .modal-footer {
        padding: 0.5rem;
    }
</style>

<div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
    {#if searchCompleted}
        {#if history.length > 0}
            {#each tabbedHistory as assessment}
                <div class="row">
                    <div class="col-sm-12">
                        <div class="card tabbed-card">
                            <button on:click="{() => {assessment = assessment; selectAssessment(assessment)}}"
                                class="button-reset focus-outline card-head" class:collapsed={!assessment.active}>

                                {#if assessment.indicatorTargeted}
                                    <img id="target-indicator-{assessment.riskFactorUrn}" class="target-indicator" src="/assets/target-indicator.svg" alt="{todaysConfig.inmateLabelName} was targeted on this assessment" />
                                    <Tooltip target="target-indicator-{assessment.riskFactorUrn}" placement="bottom">{todaysConfig.inmateLabelName} was targeted on this assessment.</Tooltip>
                                {/if}

                                {#if assessment.level == 'H'}
                                    <span id="risk-factor-level-badge-{assessment.riskFactorUrn}" class="badge badge-level badge-danger">High</span>
                                {/if}
                                {#if assessment.level == 'M'}
                                    <span id="risk-factor-level-badge-{assessment.riskFactorUrn}" class="badge badge-level badge-warning">Medium</span>
                                {/if}
                                {#if assessment.level == 'L'}
                                    <span id="risk-factor-level-badge-{assessment.riskFactorUrn}" class="badge badge-level badge-success">Low</span>
                                {/if}
                                <Tooltip target="risk-factor-level-badge-{assessment.riskFactorUrn}" placement="bottom">
                                    Total Weighted Score: {assessment.weightedPercentage}%
                                </Tooltip>

                                {#if assessment.indicatorTrend == 'U'}
                                    <img id="inmate-trend-increase-{assessment.riskFactorUrn}" src="/assets/arrow/arrow-up-danger.svg" class="trend-arrow" alt="Combined risk factor responses are more negative than their previous assessment" />
                                    <Tooltip target="inmate-trend-increase-{assessment.riskFactorUrn}" placement="bottom">Combined risk factor responses are more negative than their previous assessment</Tooltip>
                                {:else if assessment.indicatorTrend == 'D'}
                                    <img id="inmate-trend-decrease-{assessment.riskFactorUrn}" src="/assets/arrow/arrow-down-success.svg" class="trend-arrow" alt="Combined risk factor responses are more positive than their previous assessment" />
                                    <Tooltip target="inmate-trend-decrease-{assessment.riskFactorUrn}" placement="bottom">Combined risk factor responses are more positive than their previous assessment</Tooltip>
                                {/if}

                                <span class="risk-factor-date" id="risk-factor-date-{assessment.riskFactorUrn}">
                                    {dateDescriptionStringAddExtraDescriptor(assessment.dateLastUpdatedDescription, true)}
                                </span>
                                <Tooltip target="risk-factor-date-{assessment.riskFactorUrn}" placement="bottom">
                                    Assessment completed on {formatDateTime(assessment.dateLastUpdated)}
                                </Tooltip>

                                {#if !assessment.active}
                                    <div class="progress-container ml-auto">
                                        <div class="progress">
                                            {#each assessment.overallSignificantBreakdown as factorGroup}
                                                <button class="button-reset focus-outline progress-bar" id="overall-answers-{factorGroup.id}-{assessment.riskFactorUrn}"
                                                    style="background-color:{factorGroup.colour}; width: {factorGroup.percentage}%"
                                                    on:click|stopPropagation={() => (selectedSeverityGroup === factorGroup.id) ? selectedSeverityGroup = "" : selectedSeverityGroup = factorGroup.id}>
                                                    <span>{factorGroup.total}</span>
                                                </button>

                                                <Tooltip target="answers-{factorGroup.id}-{assessment.riskFactorUrn}" placement="bottom">
                                                    {factorGroup.title} ({Math.round(factorGroup.percentage)}%)
                                                </Tooltip>
                                            {/each}
                                        </div> 
                                    </div>
                                {/if}
                                
                                {#if assessment.active}
                                    <img class="card-collapse-control" src="/assets/arrow/chevron-down.svg" alt="Collapse Card" />
                                {:else}
                                    <img class="card-collapse-control" src="/assets/arrow/chevron-right.svg" alt="Expand Card" />
                                {/if}
                            </button>
                            {#if assessment.active}
                                <div transition:slide|local class="card-body">
                                    <nav>
                                        <div class="nav nav-tabs" id="nav-tab" role="tablist">
                                            {#each assessment.tabs as tab}
                                                <button on:click={selectTab(assessment, tab.id)} role="tab" aria-selected="{tab.active}"
                                                        class="nav-link focus-outline" class:active={tab.active} type="button">
                                                    {tab.name}
                                                </button>
                                            {/each}

                                            {#if selectedTabId != summaryTabId}
                                                <div class="progress-container ml-auto">
                                                
                                                    <div class="progress">
                                                        {#each getSigBreakdownForTab(assessment.riskFactorUrn, selectedTabId) as factorGroup}
                                                            <button class="button-reset focus-outline progress-bar" id="answers-{factorGroup.id}-{assessment.riskFactorUrn}"
                                                                style="background-color:{factorGroup.colour}; width: {factorGroup.percentage}%"
                                                                on:click|stopPropagation={() => (selectedSeverityGroup === factorGroup.id) ? selectedSeverityGroup = "" : selectedSeverityGroup = factorGroup.id}>
                                                                <span>{factorGroup.total}</span>
                                                            </button>

                                                            <Tooltip target="answers-{factorGroup.id}-{assessment.riskFactorUrn}" placement="bottom">
                                                                {factorGroup.title} ({Math.round(factorGroup.percentage)}%)
                                                            </Tooltip>
                                                        {/each}
                                                    </div> 
                                                
                                                </div>
                                            {/if}
                                        </div>                               
                                    </nav>

                                    <div class="card-columns-container">
                                        {#if selectedTabId === summaryTabId}
                                            <div class="card-chart-container">
                                                <canvas id="risk-factors-summary-chart-canvas-{assessment.riskFactorUrn}"></canvas>
                                            </div>
                                            <div class="overall-card-column">
                                                {#each assessment.displayedSummaryAnswers as answer}
                                                        {#if answer.header}
                                                            <div class="card-column-row" class:card-column-section-header={answer.header}>
                                                                <span>{answer.question}</span>
                                                            </div>
                                                        {:else}                           
                                                            {#if answer.notes}
                                                                {#if answer.immediateTargeted}
                                                                    <div class="badge-notched-icon-group" id="answers-{answer.clitemUrn}"
                                                                        use:tooltip={{
                                                                            content: {
                                                                                component: AimTooltip ,
                                                                                props: getRiskFactorsTooltip(answer, true)
                                                                            },
                                                                            ...getTooltipDisplayOptions({ position: "bottom", maxWidth: 600 })
                                                                        }}>
                                                                        <div class="badge-notched-icon"
                                                                            class:badge-notched-icon-warning={answer.severity == 'L'}
                                                                            class:badge-notched-icon-caution={answer.severity == 'M'}
                                                                            class:badge-notched-icon-danger={answer.severity == 'H'}>
                                                                            <img src="/assets/target-indicator-inverse.svg" alt="Immediate Targeting" />
                                                                        </div>
                                                                        <span class="badge badge-notched">
                                                                            {answer.question}
                                                                        </span>                               
                                                                    </div>
                                                                {:else}
                                                                    <span id="answers-{answer.clitemUrn}" class="badge badge-notched"
                                                                            class:badge-notched-warning={answer.severity == 'L'}
                                                                            class:badge-notched-caution={answer.severity == 'M'}
                                                                            class:badge-notched-danger={answer.severity == 'H'}
                                                                            use:tooltip={{
                                                                                content: {
                                                                                    component: AimTooltip ,
                                                                                    props: getRiskFactorsTooltip(answer, true)
                                                                                },
                                                                                ...getTooltipDisplayOptions({ position: "bottom", maxWidth: 600 })
                                                                            }}>
                                                                        {answer.question}
                                                                    </span>
                                                                {/if}
                                                            {:else}                     
                                                                {#if answer.immediateTargeted}
                                                                    <div class="badge-notched-icon-group" id="answers-{answer.clitemUrn}">
                                                                        <div class="badge-notched-icon"
                                                                            class:badge-notched-icon-warning={answer.severity == 'L'}
                                                                            class:badge-notched-icon-caution={answer.severity == 'M'}
                                                                            class:badge-notched-icon-danger={answer.severity == 'H'}>
                                                                            <img src="/assets/target-indicator-inverse.svg" alt="Immediate Targeting" />
                                                                        </div>
                                                                        <span class="badge badge-notched">
                                                                            {answer.question}
                                                                        </span>                               
                                                                    </div>
                                                                {:else}
                                                                    <span id="answers-{answer.clitemUrn}" class="badge badge-notched"
                                                                            class:badge-notched-warning={answer.severity == 'L'}
                                                                            class:badge-notched-caution={answer.severity == 'M'}
                                                                            class:badge-notched-danger={answer.severity == 'H'}>
                                                                        {answer.question}
                                                                    </span>
                                                                {/if}
                                                                {#if answer.displayanswercontext}
                                                                    <Tooltip target="answers-{answer.clitemUrn}" placement="bottom">{answer.answer}</Tooltip>
                                                                {/if}
                                                            {/if}
                                                        {/if}
                                                {/each}
                                            </div>
                                        {:else}
                                        <div class="card-columns">
                                            {#each assessment.displayedAnswers as answers}
                                                <div class="card-column">
                                                     {#each answers as answer}
                                                        {#if answer.immediateTargeted}
                                                            <div class="well" id="immediate-target-{answer.clitemUrn}">
                                                                <div class="well-icon"
                                                                    class:well-icon-warning={answer.severity == 'L'}
                                                                    class:well-icon-caution={answer.severity == 'M'}
                                                                    class:well-icon-danger={answer.severity == 'H'}>
                                                                    <img src="/assets/target-indicator-inverse.svg" alt="Immediate Targeting" />
                                                                </div>
                                                                <div class="card-column-row" 
                                                                    class:selected-row="{selectedSeverityGroup && answer.severity == selectedSeverityGroup}"
                                                                    class:hidden-row="{selectedSeverityGroup && answer.severity != selectedSeverityGroup}">
                                                                    <span>{answer.question}</span>                                                                
                                                                    <div class="icon-container">
                                                                        <span class:no-sig-text={answer.severity == 'NA'} 
                                                                            class:low-sig-text={answer.severity == 'L'}
                                                                            class:med-sig-text={answer.severity == 'M'}
                                                                            class:high-sig-text={answer.severity == 'H'} 
                                                                            class:icon-offset={answer.notes && answer.notes != 'No comments'}>
                                                                            {answer.answer}
                                                                        </span>
                                                                        {#if answer.notes && answer.notes != 'No comments'}
                                                                            <button id="notes-{assessment.assessmentUrn}-{answer.section}-{answer.order}" type="button" class="button-reset focus-outline"
                                                                            on:click={() => selectedComment = answer}
                                                                            use:tooltip={{
                                                                                content: {
                                                                                    component: AimTooltip ,
                                                                                    props: getRiskFactorsTooltip(answer, false)
                                                                                },
                                                                                ...getTooltipDisplayOptions({ position: "left", maxWidth: 600 })
                                                                            }}>
                                                                                <img src="/assets/notes-fill.svg" alt="{answer.notes}" />
                                                                            </button>
                                                                        {/if}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        {:else}
                                                            <div class="card-column-row" 
                                                                class:selected-row="{selectedSeverityGroup && answer.severity == selectedSeverityGroup}"
                                                                class:hidden-row="{selectedSeverityGroup && answer.severity != selectedSeverityGroup}">
                                                                <span>{answer.question}</span>
                                                                    <div class="icon-container">
                                                                    <span class:no-sig-text={answer.severity == 'NA'} 
                                                                        class:low-sig-text={answer.severity == 'L'}
                                                                        class:med-sig-text={answer.severity == 'M'}
                                                                        class:high-sig-text={answer.severity == 'H'}
                                                                        class:icon-offset={answer.notes && answer.notes != 'No comments'}>
                                                                        {answer.answer}
                                                                    </span>
                                                                    {#if answer.notes && answer.notes != 'No comments'}
                                                                        <button id="notes-{assessment.assessmentUrn}-{answer.section}-{answer.order}" type="button" class="button-reset focus-outline"
                                                                        on:click={() => selectedComment = answer}
                                                                        use:tooltip={{
                                                                            content: {
                                                                                component: AimTooltip ,
                                                                                props: getRiskFactorsTooltip(answer, false)
                                                                            },
                                                                            ...getTooltipDisplayOptions({ position: "left", maxWidth: 600 })
                                                                        }}>
                                                                            <img src="/assets/notes-fill.svg" alt="{answer.notes}" />
                                                                        </button>
                                                                    {/if}
                                                                </div>
                                                            </div>
                                                        {/if}
                                                    {/each}
                                                </div>
                                            {/each}
                                        </div>
                                        {/if}
                                    </div>

                                    <div class="risk-factor-details">
                                        <div class="risk-factor-detail">
                                            {#if todaysConfig.showSiteName && assessment.siteUrn}
                                                {getReferenceDataSiteName(assessment.siteUrn)} 
                                            {/if}
                                        
                                            {#if assessment.unit || assessment.wing || assessment.cell}
                                                {#if assessment.unit}{assessment.unit}{/if}
                                                {#if assessment.unit && assessment.wing}&nbsp;&middot;&nbsp;{/if}
                                                {#if assessment.wing}{assessment.wing}{/if}
                                                {#if (assessment.unit || assessment.wing) && assessment.cell}&nbsp;&middot;&nbsp;{/if}
                                                {#if assessment.cell}{assessment.cell}{/if}
                                            {:else}
                                                Location Not Recorded
                                            {/if}

                                            {#if assessment.vptCellStatusUrn > 0}
                                                <img id="risk-factor-detail-cell-status-indicator-{assessment.riskFactorUrn}" src="/assets/{getReferenceDataItemFromList(assessment.vptCellStatusUrn, $referenceDataCellStatusList, 'iconName', 'vptCellStatusUrn')}.svg" alt="{getReferenceDataItemFromList(assessment.vptCellStatusUrn, $referenceDataCellStatusList, 'description', 'vptCellStatusUrn')}" />
                                                <Tooltip target="risk-factor-detail-cell-status-indicator-{assessment.riskFactorUrn}" placement="bottom">
                                                    {getReferenceDataItemFromList(assessment.vptCellStatusUrn, $referenceDataCellStatusList, 'description', 'vptCellStatusUrn')}
                                                </Tooltip>
                                            {/if}
                                        </div>
                                        
                                        <div class="risk-factor-detail">
                                            {#if assessment.keyworkerName}
                                                <span id="risk-factor-detail-keyworker-{assessment.riskFactorUrn}">{assessment.keyworkerName} (Since {formatDate(assessment.keyworkerStartDate)})</span>
                                                <Tooltip target="risk-factor-detail-keyworker-{assessment.riskFactorUrn}" placement="bottom">{todaysConfig.keyworkerLabelName}</Tooltip>
                                            {:else}
                                                {todaysConfig.keyworkerLabelName} Not Recorded
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        {:else}
            <div class="alert alert-danger" role="alert">No Risk Factor assessments have been completed.</div>
        {/if}
    {:else}
        <div class="spinner-container">
            <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
            <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
        </div>
    {/if}
</div>

{#if selectedComment}
    <div class="modal" tabindex="-1" role="dialog" aria-label="Comments">
        <div in:fly|local={{ y: -100 }} class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Comments</h5>
                    
                    <button type="button" class="close" aria-label="Close" on:click={() => selectedComment = null}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    {#if (selectedComment.questionHelpText)}
                        <div class="prompt">
                            {selectedComment.questionHelpText}
                        </div>
                    {/if}
                    <div>
                        <span>{selectedComment.notes}</span>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" on:click={() => selectedComment = null}>Close</button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal-backdrop show"></div>
{/if}
